import { createClient } from '@supabase/supabase-js';

// The publishable key is meant to ship in the browser: on its own it grants
// nothing, because row-level security decides what any given user can read.
export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'mh-auth',
    },
  },
);

/* ── auth ────────────────────────────────────────────────────────────────── */

export const signInWithGoogle = () =>
  supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });

export const signInWithEmail = (email) =>
  supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin },
  });

// Global scope revokes every session everywhere, not just this browser. This is
// the realistic answer to a lost phone, which is why it is a first-class action
// rather than buried in settings.
export const signOutEverywhere = () => supabase.auth.signOut({ scope: 'global' });
export const signOut = () => supabase.auth.signOut();

/* ── transient failures ──────────────────────────────────────────────────── */

// Supabase Auth mints the token and PostgREST validates it; a few seconds of
// clock skew between them yields "JWT issued at future", and a refresh that
// lands mid-request yields a brief 401. Both clear themselves within seconds.
// Retrying once beats showing the traveller an error they can only escape by
// force-quitting.
const TRANSIENT = /issued at future|used before issued|jwt expired|token is expired|clock|fetch failed|network|502|503|504/i;

export const isAuthError = (e) =>
  /jwt|token|unauthor|401/i.test(e?.message ?? '') && !/permission denied/i.test(e?.message ?? '');

async function retrying(fn, { attempts = 3, base = 700 } = {}) {
  let last;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      last = e;
      if (!TRANSIENT.test(e?.message ?? '')) throw e;
      // A refresh often fixes a token the server has just rejected.
      if (i === 0 && isAuthError(e)) {
        try { await supabase.auth.refreshSession(); } catch { /* keep going */ }
      }
      await new Promise((r) => setTimeout(r, base * (i + 1)));
    }
  }
  throw last;
}

/* ── reads ───────────────────────────────────────────────────────────────── */

export const fetchTrips = () => retrying(_fetchTrips);
async function _fetchTrips() {
  const { data, error } = await supabase
    .from('trips')
    .select('id, external_key, title, destination, start_date, end_date, status, home_currency, summary, offline_enabled')
    .order('start_date', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

// One round trip for the whole trip. It is a lot at once, but every screen needs
// cross-cutting slices of it (a day needs events + blocks + documents), and a
// trip is a few hundred rows at most — far cheaper than a request per tab.
export const fetchTrip = (tripId) => retrying(() => _fetchTrip(tripId));
async function _fetchTrip(tripId) {
  const many = async (table, order) => {
    let q = supabase.from(table).select('*').eq('trip_id', tripId);
    if (order) q = q.order(order, { ascending: true, nullsFirst: false });
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
  };

  const [trip, stops, events, dayNotes, shortlists, items, deadlines,
         checklist, blocks, documents, contacts, fx, eventTravelers,
         visits, visitPhotos] =
    await Promise.all([
      supabase.from('trips').select('*').eq('id', tripId).single()
        .then(({ data, error }) => { if (error) throw error; return data; }),
      many('stops', 'sort_order'),
      many('events', 'event_date'),
      many('day_notes', 'date'),
      many('shortlists'),
      many('shortlist_items', 'rank'),
      many('deadlines', 'due_at'),
      many('checklist_items', 'sort_order'),
      many('blocks', 'sort_order'),
      many('documents'),
      many('contacts', 'sort_order'),
      many('trip_fx'),
      many('event_travelers'),
      many('visits', 'visited_at'),
      many('visit_photos', 'sort_order'),
    ]);

  const { data: files, error: fErr } = await supabase
    .from('document_files').select('*').eq('trip_id', tripId).order('sort_order');
  if (fErr) throw fErr;

  // places, vendors and travellers are account-scoped rather than trip-scoped,
  // so they are fetched separately and joined client-side.
  const [{ data: places }, { data: vendors }, { data: travelers }] = await Promise.all([
    supabase.from('places').select('*'),
    supabase.from('vendors').select('*'),
    supabase.from('travelers').select('*'),
  ]);

  const byId = (rows) => new Map((rows ?? []).map((r) => [r.id, r]));
  const placeMap = byId(places);
  const vendorMap = byId(vendors);
  const travelerMap = byId(travelers);

  // Seat and ticket number live on the join, because they differ per person on
  // the same flight — and the join was being dropped on the floor here, which is
  // why an agent could write four seat numbers that nothing would ever show.
  const paxByEvent = new Map();
  for (const et of eventTravelers) {
    if (!paxByEvent.has(et.event_id)) paxByEvent.set(et.event_id, []);
    const t = travelerMap.get(et.traveler_id);
    if (t) {
      paxByEvent.get(et.event_id).push({
        ...t, seat: et.seat, ticket_number: et.ticket_number,
        baggage: et.baggage, note: et.note,
      });
    }
  }

  // A document is the logical item; its files are the parts. Four boarding
  // passes live under one "Carte d'imbarco" slot.
  const filesByDoc = new Map();
  for (const f of files ?? []) {
    if (!filesByDoc.has(f.document_id)) filesByDoc.set(f.document_id, []);
    filesByDoc.get(f.document_id).push({
      ...f, traveler: travelerMap.get(f.traveler_id) ?? null,
    });
  }
  const docs = documents.map((d) => ({ ...d, files: filesByDoc.get(d.id) ?? [] }));

  // The diary. A visit carries its photos and, where it had one, the place —
  // so an entry can be mapped and read without going back to the plan it came
  // from, which may well have been deleted since.
  const photosByVisit = new Map();
  for (const p of visitPhotos) {
    if (!photosByVisit.has(p.visit_id)) photosByVisit.set(p.visit_id, []);
    photosByVisit.get(p.visit_id).push(p);
  }
  const diary = visits.map((v) => ({
    ...v,
    place: placeMap.get(v.place_id) ?? null,
    photos: photosByVisit.get(v.id) ?? [],
  }));
  const visitByEvent = new Map(diary.filter((v) => v.event_id).map((v) => [v.event_id, v]));

  const hydrated = events.map((e) => ({
    ...e,
    place: placeMap.get(e.place_id) ?? null,
    vendor: vendorMap.get(e.vendor_id) ?? null,
    travelers: paxByEvent.get(e.id) ?? [],
    documents: docs.filter((d) => d.event_id === e.id),
    blocks: blocks.filter((b) => b.parent_type === 'event' && b.parent_id === e.id),
    // Whether this actually happened, and when — the card's own answer, so it
    // does not have to be threaded down through every screen that renders one.
    visit: visitByEvent.get(e.id) ?? null,
  }));

  return {
    trip,
    stops,
    events: hydrated,
    dayNotes,
    shortlists: shortlists.map((s) => ({
      ...s,
      stop: stops.find((st) => st.id === s.stop_id) ?? null,
      items: items
        .filter((i) => i.shortlist_id === s.id)
        .map((i) => ({ ...i, place: placeMap.get(i.place_id) ?? null,
                             vendor: vendorMap.get(i.vendor_id) ?? null })),
    })),
    deadlines: deadlines.map((d) => ({
      ...d, event: hydrated.find((e) => e.id === d.event_id) ?? null,
    })),
    checklist,
    blocks,
    documents: docs,
    contacts,
    visits: diary,
    rates: Object.fromEntries(fx.map((r) => [r.currency, Number(r.rate)])),
    places: places ?? [],
    vendors: vendors ?? [],
    travelers: travelers ?? [],
  };
}

/* ── sharing ─────────────────────────────────────────────────────────────── */
//  An invitation is a proposal: the row exists but grants nothing until it is
//  accepted, so an invited trip is invisible to fetchTrips and fetchTrip. These
//  RPCs are the only way to see one, which is why they are SECURITY DEFINER on
//  the database side and filter on auth.uid() themselves.

export const fetchInvitations = () => retrying(_fetchInvitations);
async function _fetchInvitations() {
  const { data, error } = await supabase.rpc('list_trip_invitations');
  if (error) throw error;
  return data ?? [];
}

// trip_id rather than the key, because external_key is unique only per creator:
// two people could each invite you to a trip they both call the same thing.
export async function respondToInvitation(tripId, accept) {
  const { data, error } = await supabase.rpc('respond_to_trip_invitation',
    { p_trip_id: tripId, p_accept: accept });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error);
  return data;
}

export const fetchMembers = (tripKey) => retrying(() => _fetchMembers(tripKey));
async function _fetchMembers(tripKey) {
  const { data, error } = await supabase.rpc('list_trip_members', { p_trip_key: tripKey });
  if (error) throw error;
  return data ?? [];
}

export async function inviteMember(tripKey, email, role = 'editor') {
  const { data, error } = await supabase.rpc('invite_trip_member',
    { p_trip_key: tripKey, p_email: email, p_role: role });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error);
  return data;
}

export async function removeMember(tripKey, email) {
  const { data, error } = await supabase.rpc('remove_trip_member',
    { p_trip_key: tripKey, p_email: email });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error);
  return data;
}

/* ── writes the app owns ─────────────────────────────────────────────────── */

export async function setChecklistDone(id, done) {
  const { error } = await supabase.from('checklist_items').update({ done }).eq('id', id);
  if (error) throw error;
}

export async function setDeadlineDone(id, done) {
  const { error } = await supabase.from('deadlines').update({ done }).eq('id', id);
  if (error) throw error;
}

// Removing something from the plan. Deliberately a plain delete: the database
// says what happens to everything hanging off it — legs go with their journey,
// deadlines go with their booking, documents and diary entries are cut loose
// rather than destroyed — and the confirmation the user reads is built from the
// same facts, so what they are told is what occurs.
export async function deleteEvent(event) {
  const { error } = await supabase.from('events').delete().eq('id', event.id);
  if (error) throw error;
}

/* ── deciding ────────────────────────────────────────────────────────────── */

// The promotion logic — mark chosen, reject the rest, put it on the timeline —
// lives in the database so the app and the MCP server cannot drift apart. RLS
// governs it, because the function runs as the caller.
export async function chooseOption(itemId, opts = {}) {
  const { data, error } = await supabase.rpc('choose_option', {
    p_item_id: itemId,
    p_event_date: opts.event_date ?? null,
    p_starts_at: opts.starts_at ?? null,
    p_timezone: opts.timezone ?? null,
    p_time_note: opts.time_note ?? null,
    p_title: opts.title ?? null,
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error);
  return data;
}

// Moving a decided thing to another day is not the same as undeciding it.
// Reopening deletes the event — with its deadlines, its documents' link to it
// and anything edited on it since — which is far too much to pay for "not
// Tuesday, Wednesday". So the date is changed on the event itself and
// everything hanging off it stays attached.
//
// Passing null for a field clears it: a move to a day with no time given has to
// be able to drop the old clock, or 08:30 survives onto a day it was never
// agreed for.
export async function moveEvent(event, { event_date, starts_at, timezone, time_note }) {
  const patch = { event_date: event_date ?? null, starts_at: starts_at ?? null };
  // The zone belongs to the clock; with no clock there is nothing for it to
  // qualify, and a stale zone is what silently moves an event across midnight.
  patch.timezone = starts_at ? (timezone ?? null) : null;
  if (time_note !== undefined) patch.time_note = time_note;

  // An end is a duration from the start, not a fact about a date: a two-hour
  // trek moved to Thursday still lasts two hours. Left untouched it would end
  // the day before it begins.
  if (event.ends_at) {
    patch.ends_at = starts_at && event.starts_at
      ? new Date(Date.parse(event.ends_at)
                 + (Date.parse(starts_at) - Date.parse(event.starts_at))).toISOString()
      : null;                       // no clock left to hang a duration on
  }

  const { error } = await supabase.from('events').update(patch).eq('id', event.id);
  if (error) throw error;
}

// Discarding a candidate keeps it: "we are not doing this" is a decision worth
// being able to read later, and worth being able to take back on the road. The
// row stays, its status changes.
export async function setOptionStatus(itemId, status) {
  const { error } = await supabase.from('shortlist_items').update({ status }).eq('id', itemId);
  if (error) throw error;
}

export async function unchooseOption(itemId) {
  const { data, error } = await supabase.rpc('unchoose_option', { p_item_id: itemId });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error);
  return data;
}

// One private bucket for everything the trip carries: the paperwork under a
// document id, the diary's photos under a visit id. Trip membership is read
// from segment 2 of the path either way, so there is one storage rule.
const BUCKET = 'trip-docs';

/* ── the diary ───────────────────────────────────────────────────────────── */
//  Logging a visit has to be one tap on a phone, standing outside the thing you
//  have just seen. So everything is defaulted: the time is now, the zone is the
//  one the phone is in — which, while travelling, is the one you are actually
//  standing in — and the title comes from whatever you tapped.

export const phoneZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;

export async function logVisit({ trip_id, title, event_id = null, place_id = null,
                                 stop_id = null, visited_at = null, timezone = null,
                                 note = null }) {
  const { data, error } = await supabase.from('visits').insert({
    trip_id, title, event_id, place_id, stop_id,
    visited_at: visited_at ?? new Date().toISOString(),
    timezone: timezone ?? phoneZone(),
    note,
    source: 'app',
  }).select().single();
  if (error) throw error;
  return data;
}

export async function updateVisit(id, patch) {
  const { error } = await supabase.from('visits').update(patch).eq('id', id);
  if (error) throw error;
}

// Un-marking something is a real deletion, photos included — a diary entry that
// says "we did not do this after all" is not a thing anyone wants to read. The
// objects go first, because a leaked file is worse than a leaked row: nothing
// in the app would ever list it again.
export async function deleteVisit(visit) {
  const paths = (visit.photos ?? []).map((p) => p.storage_path).filter(Boolean);
  if (paths.length) await supabase.storage.from(BUCKET).remove(paths);
  const { error } = await supabase.from('visits').delete().eq('id', visit.id);
  if (error) throw error;
}

// Evidence, not an album. The cap is here rather than in the database because
// it is a judgement about the app, not about the data — but it is enforced, or
// "just for tracking" quietly becomes a photo library with no way back.
export const MAX_VISIT_PHOTOS = 3;

export async function uploadVisitPhotos(visit, fileList, onProgress) {
  const existing = visit.photos?.length ?? 0;
  const files = Array.from(fileList).slice(0, Math.max(0, MAX_VISIT_PHOTOS - existing));
  if (files.length === 0) {
    throw new Error(`Bastano ${MAX_VISIT_PHOTOS} foto per tappa — questa le ha già.`);
  }

  const added = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const safeName = file.name.replace(/[^\w.\-]+/g, '_') || `foto-${i + 1}.jpg`;
    const n = existing + i + 1;
    const path = `trips/${visit.trip_id}/visits/${visit.id}/${n}-${safeName}`;

    onProgress?.({ index: i, total: files.length, name: file.name });

    const { error: upErr } = await supabase.storage
      .from(BUCKET).upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) throw new Error(`${file.name}: ${upErr.message}`);

    const { data, error } = await supabase.from('visit_photos').insert({
      visit_id: visit.id,
      trip_id: visit.trip_id,
      storage_path: path,
      file_name: safeName,
      mime_type: file.type || null,
      size_bytes: file.size,
      taken_at: file.lastModified ? new Date(file.lastModified).toISOString() : null,
      sort_order: n,
    }).select().single();
    if (error) throw error;
    added.push(data);
  }
  return added;
}

export async function deleteVisitPhoto(photo) {
  const { error } = await supabase.from('visit_photos').delete().eq('id', photo.id);
  if (error) throw error;
  await supabase.storage.from(BUCKET).remove([photo.storage_path]);
}

/* ── documents ───────────────────────────────────────────────────────────── */

// Never a public URL. A signed link is minted per view, expires quickly, and is
// only issued because RLS already confirmed this user is a member of the trip.
export async function fileUrl(file, seconds = 300) {
  if (!file?.storage_path) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET).createSignedUrl(file.storage_path, seconds);
  if (error) throw error;
  return data.signedUrl;
}

// Adds files to a slot the agent already declared, keeping its title and its
// link to the booking. Several at once, because one slot routinely means four
// boarding passes. Path: trips/{trip_id}/{document_id}/{n}-{file_name}
export async function uploadDocumentFiles(doc, fileList, onProgress) {
  const files = Array.from(fileList);
  const existing = doc.files?.length ?? 0;
  const added = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const safeName = file.name.replace(/[^\w.\-]+/g, '_') || `file-${i + 1}`;
    const n = existing + i + 1;
    const path = `trips/${doc.trip_id}/${doc.id}/${n}-${safeName}`;

    onProgress?.({ index: i, total: files.length, name: file.name });

    const { error: upErr } = await supabase.storage
      .from(BUCKET).upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) throw new Error(`${file.name}: ${upErr.message}`);

    const { data, error } = await supabase.from('document_files').insert({
      document_id: doc.id,
      trip_id: doc.trip_id,
      storage_path: path,
      file_name: safeName,
      mime_type: file.type || null,
      size_bytes: file.size,
      sort_order: n,
    }).select().single();
    if (error) throw error;
    added.push(data);
  }
  return added;   // documents.status flips to 'present' by trigger
}

// Removing the row and the object are separate operations; the row is what the
// app reads, so it goes first and a leftover object is harmless.
export async function deleteDocumentFile(file) {
  const { error } = await supabase.from('document_files').delete().eq('id', file.id);
  if (error) throw error;
  await supabase.storage.from(BUCKET).remove([file.storage_path]);
}
